import { getField } from "@simplyk/common";

import { useLiveFormEditorContext } from "../LiveFormEditorContext";

import { useCurrentUserContext } from "@/common/contexts/CurrentUserContext";
import { DonationFormFieldOutput, DonationFormPreviewOutput } from "@/common/types/trpc";
import { useBuildPrefilledDescription } from "@/features/LiveFormEditor/common/prefillDataBuilders/useBuildPrefilledDescription";
import { defaultLiveFormEditorPreviewPlaceholders } from "@/features/LiveFormEditor/LivePreview/contexts/PreviewContext";
import { DEFAULT_AUTOMATED_EMAIL_OPENED_CARD } from "@/features/LiveFormEditor/Steps/StepAutomatedCommunications/types";
import { LiveDonationData } from "@/features/LiveFormEditor/types/LiveFormEditor.types";

export const useBuildInitialLiveDonationData = () => {
  const { stepProgress, getIndexStepFromKey } = useLiveFormEditorContext();
  const furthestStepReached = stepProgress.reached;
  const { organization: currentOrganization } = useCurrentUserContext();
  const { buildPrefilledDescription } = useBuildPrefilledDescription();

  const buildInitialLiveDonationData = (apiFormObject: DonationFormPreviewOutput): LiveDonationData => {
    // If we haven't passed the fundraising goal step yet, show the default target
    const target =
      furthestStepReached < getIndexStepFromKey("fundraising-goal") ? 100000 : apiFormObject.target || null;

    const organizationName = currentOrganization?.name || "";

    const defaultLocaleField = getField(apiFormObject.donationFormFields, apiFormObject.locale);

    // If the user has not reached the Description step yet, we display a prefilled description
    const description =
      furthestStepReached < getIndexStepFromKey("description")
        ? buildPrefilledDescription({
            organizationName: organizationName || "",
            isPeerToPeerCampaign: false,
          })
        : defaultLocaleField?.description || "";

    const hasReceipt = furthestStepReached < getIndexStepFromKey("tax-receipts") || apiFormObject.hasReceipt;

    return {
      donation: {
        ...apiFormObject,
        target,
        donationFormFields: [{ ...defaultLocaleField, description } as DonationFormFieldOutput],
        hasReceipt,
        organization: {
          ...apiFormObject.organization,
          name: organizationName,
        },
      },
      placeholders: {
        ...defaultLiveFormEditorPreviewPlaceholders,
        isLogo: furthestStepReached < getIndexStepFromKey("logo"),
        isDescription: furthestStepReached < getIndexStepFromKey("description"),
        isBuyerQuestions: furthestStepReached < getIndexStepFromKey("questions"),
        isFundraisingGoal: furthestStepReached < getIndexStepFromKey("fundraising-goal"),
      },
      automatedCommunicationsData: {
        emailInPreview: DEFAULT_AUTOMATED_EMAIL_OPENED_CARD,
        thankYou: null,
        thankYouBidPlaced: null,
        thankYouNonWinner: null,
        reminder1: null,
        reminder2: null,
        expiringMembership: null,
        autoRenewal: null,
        readyForPickup: null,
        orderShipped: null,
      },
    };
  };

  return { buildInitialLiveDonationData };
};
